/**
 * The shortcuts sheet.
 *
 * Rendered from the binding registry rather than written out, so it cannot
 * claim a key that does not exist. A hand-written sheet drifts within a week,
 * and it drifts in the worst direction: telling someone a shortcut works when
 * it was renamed or removed.
 *
 * A `<dialog>`, not a bespoke overlay. `showModal()` brings the focus trap, the
 * Escape handling, the inert background and the top-layer stacking for free —
 * all four of which are things a hand-rolled modal gets subtly wrong.
 */
import { type Binding, formatKeys } from "./keys";

let dialog: HTMLDialogElement | null = null;

export function shortcutsSheet(bindings: readonly Binding[], isMac: boolean) {
  function build(): HTMLDialogElement {
    if (dialog) return dialog;

    const el = document.createElement("dialog");
    // `floating` carries the elevation. A raw box-shadow here is what the design
    // system tripwire exists to catch — shadows are reserved for things that
    // actually float, and this one does.
    el.className = "sheet floating";
    el.setAttribute("aria-label", "Keyboard shortcuts");

    const head = document.createElement("div");
    head.className = "sheet-head";
    const title = document.createElement("h2");
    title.className = "sheet-title";
    title.textContent = "Keyboard shortcuts";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "icon-btn";
    close.setAttribute("aria-label", "Close");
    close.textContent = "✕";
    close.addEventListener("click", () => el.close());
    head.appendChild(title);
    head.appendChild(close);
    el.appendChild(head);

    const body = document.createElement("div");
    body.className = "sheet-body";

    // Section order follows first appearance in the registry, so the order the
    // bindings are declared in is the order they are taught in.
    const groups = new Map<string, Binding[]>();
    for (const b of bindings) {
      if (b.alias) continue;
      const list = groups.get(b.group);
      if (list) list.push(b);
      else groups.set(b.group, [b]);
    }

    for (const [group, list] of groups) {
      const section = document.createElement("section");
      section.className = "sheet-group";

      const heading = document.createElement("h3");
      heading.textContent = group;
      section.appendChild(heading);

      const dl = document.createElement("dl");
      for (const b of list) {
        const dt = document.createElement("dt");
        dt.textContent = b.label;
        const dd = document.createElement("dd");
        const kbd = document.createElement("kbd");
        kbd.textContent = formatKeys(b, isMac);
        dd.appendChild(kbd);
        dl.appendChild(dt);
        dl.appendChild(dd);
      }
      section.appendChild(dl);
      body.appendChild(section);
    }

    el.appendChild(body);
    // Clicking the backdrop closes, which people try before they look for an ✕.
    // The check is against the dialog itself because the backdrop is not an
    // element — a click on it reports the dialog as the target.
    el.addEventListener("click", (e) => {
      if (e.target === el) el.close();
    });

    document.body.appendChild(el);
    dialog = el;
    return el;
  }

  return {
    toggle() {
      const el = build();
      if (el.open) el.close();
      else el.showModal();
    },
    close() {
      dialog?.close();
    },
  };
}
